import React from 'react';
import { withRouter } from 'react-router-dom';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import CONSTANT from '../../constant';
import Loader from '../../component/common/Loader';
import {
    getSelfAppraisal,
    saveSelfAppraisal,
    updateSelfAppraisal,
} from '../../actions/AppraisalCycleAction';
import { getPropertyReturnList } from '../../actions/property_retrun';
import { formatDateDDMMYY } from '../../utils';

class SelfAppraisalDetails extends React.Component<any, any> {
    constructor(props: any) {
        super(props);
        this.state = {
            showLoader: false,
            isSubmitted: false,
            selfAppraisalId: null,

            briefResponsibilities: '',
            exceptionalContribution: '',
            constraintsHindered: '',
            trainingCurrentAssignment: '',
            trainingFutureCareer: '',

            immovablePropertyFiled: '',
            immovablePropertyDate: '',
            medicalCheckUp: '',
            medicalReportFile: null as File | null,
            medicalReportFileName: '',
            annualWorkPlanSet: '',

            // Property return filed via /emp/property-return for this cycle's year (null = not filed)
            propertyReturnYear: '',
            propertyReturnRecord: null as any,

            showAppendixModal: false,
            validationErrors: {} as any,
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        document.title = 'Self Appraisal Details - ' + CONSTANT.organisation.name;

        const locationState = this.props.location?.state || {};
        if (!locationState.selectedRecord || !locationState.kpiReportId) {
            this.props.history.replace(CONSTANT.component.selfAppraisalList.url);
            return;
        }

        this.setState({ showLoader: true });

        const formatDate = (val: any) => {
            if (!val) return '';
            const d = new Date(val);
            if (isNaN(d.getTime())) return '';
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };

        // Property return year is stored as the FY start year, e.g. "2025" for FY "2025-26"
        const cycle = locationState.selectedRecord?.cycle;
        const propertyReturnYear = String(cycle?.financial_year || '').slice(0, 4);

        Promise.all([
            getSelfAppraisal({ kpiReportId: locationState.kpiReportId }),
            getPropertyReturnList().catch(() => null),
        ]).then(([res, propertyRes]: any) => {
            const data = res?.result || null;
            const propertyReturnRecord = (propertyRes?.result || []).find(
                (r: any) => String(r.year_of_property) === propertyReturnYear
            ) || null;

            // Until submitted, the declaration always reflects the actual property return filing
            const isSubmitted = !!data?.is_submitted;
            const propertyFields = isSubmitted ? {} : {
                immovablePropertyFiled: propertyReturnRecord ? 'yes' : 'no',
                immovablePropertyDate: propertyReturnRecord ? formatDate(propertyReturnRecord.created_at) : '',
            };

            this.setState({ propertyReturnYear, propertyReturnRecord });

            if (data) {
                const toBoolStr = (val: any) => val === true || val === 'yes' ? 'yes' : val === false || val === 'no' ? 'no' : '';

                this.setState({
                    selfAppraisalId: data.id,
                    isSubmitted: !!data.is_submitted,
                    briefResponsibilities: data.brief_responsibilities || '',
                    exceptionalContribution: data.exceptional_contribution || '',
                    constraintsHindered: data.constraints_hindered || '',
                    trainingCurrentAssignment: data.training_current_assignment || '',
                    trainingFutureCareer: data.training_future_career || '',
                    immovablePropertyFiled: toBoolStr(data.immovable_property_filed),
                    immovablePropertyDate: formatDate(data.immovable_property_date),
                    medicalCheckUp: toBoolStr(data.medical_check_up),
                    medicalReportFileName: data.medical_report_original_name || data.medical_report_file_name || data.medical_report_file || '',
                    annualWorkPlanSet: toBoolStr(data.annual_work_plan_set),
                    ...propertyFields,
                    showLoader: false,
                });
            } else {
                this.setState({ ...propertyFields, showLoader: false });
            }
        });
    }

    onTextChange = (e: any) => {
        const { name, value } = e.target;
        this.setState({ [name]: value });
    };

    countWords = (text: string) =>
        text.trim() === '' ? 0 : text.trim().split(/\s+/).length;

    buildPayload = (isSubmit: boolean) => {
        const locationState = this.props.location?.state || {};
        const {
            selfAppraisalId,
            briefResponsibilities,
            exceptionalContribution, constraintsHindered,
            trainingCurrentAssignment, trainingFutureCareer,
            immovablePropertyFiled, immovablePropertyDate,
            medicalCheckUp, medicalReportFile,
            annualWorkPlanSet,
        } = this.state;

        const declarationFields: any = {
            immovablePropertyFiled,
            immovablePropertyDate: immovablePropertyFiled === 'yes' ? immovablePropertyDate : '',
            medicalCheckUp,
            annualWorkPlanSet,
            isSubmit: isSubmit ? 'true' : 'false',
        };

        if (medicalCheckUp === 'yes' && medicalReportFile) {
            declarationFields.medicalReport = medicalReportFile;
        }

        const common = {
            briefResponsibilities,
            exceptionalContribution,
            constraintsHindered,
            trainingCurrentAssignment,
            trainingFutureCareer,
            ...declarationFields,
        };

        if (selfAppraisalId) {
            return { isNew: false, model: { selfAppraisalId, ...common } };
        }
        return { isNew: true, model: { kpiReportId: locationState.kpiReportId, ...common } };
    };

    handleSaveDraft = () => {
        const { isNew, model } = this.buildPayload(false);
        this.setState({ showLoader: true });

        const apiCall = isNew ? saveSelfAppraisal(model) : updateSelfAppraisal(model);
        apiCall.then((res: any) => {
            this.setState({ showLoader: false });
            if (res?.status === CONSTANT.static.apiStatus.success) {
                if (isNew) {
                    this.setState({ selfAppraisalId: res.result?.selfAppraisalId });
                }
                if (this.state.medicalReportFile) {
                    this.setState({ medicalReportFileName: this.state.medicalReportFile.name, medicalReportFile: null });
                }
                alert('Self Appraisal saved as draft.');
            }
        });
    };

    validateAllFields = () => {
        const {
            briefResponsibilities, exceptionalContribution, constraintsHindered,
            trainingCurrentAssignment, trainingFutureCareer,
            immovablePropertyFiled, immovablePropertyDate,
            medicalCheckUp, medicalReportFile, medicalReportFileName,
            annualWorkPlanSet,
        } = this.state;

        const errors: any = {};

        if (!String(briefResponsibilities).trim()) errors.briefResponsibilities = true;
        if (!String(exceptionalContribution).trim()) errors.exceptionalContribution = true;
        if (!String(constraintsHindered).trim()) errors.constraintsHindered = true;
        if (!String(trainingCurrentAssignment).trim()) errors.trainingCurrentAssignment = true;
        if (!String(trainingFutureCareer).trim()) errors.trainingFutureCareer = true;

        if (!immovablePropertyFiled) errors.immovablePropertyFiled = true;
        else if (immovablePropertyFiled === 'yes' && !immovablePropertyDate) errors.immovablePropertyDate = true;

        if (!medicalCheckUp) errors.medicalCheckUp = true;
        else if (medicalCheckUp === 'yes' && !medicalReportFile && !medicalReportFileName) errors.medicalReport = true;

        if (!annualWorkPlanSet) errors.annualWorkPlanSet = true;

        return errors;
    };

    onSubmit = (e: any) => {
        e.preventDefault();

        const errors = this.validateAllFields();
        if (Object.keys(errors).length > 0) {
            this.setState({ validationErrors: errors });
            alert('Please fill all the highlighted required fields before submitting.');
            return;
        }
        this.setState({ validationErrors: {} });

        if (!window.confirm('Are you sure you want to submit your Self Appraisal? You will not be able to edit it after submission.')) {
            return;
        }

        const { isNew, model } = this.buildPayload(true);
        this.setState({ showLoader: true });

        const apiCall = isNew ? saveSelfAppraisal(model) : updateSelfAppraisal(model);
        apiCall.then((res: any) => {
            this.setState({ showLoader: false });
            if (res?.status === CONSTANT.static.apiStatus.success) {
                if (isNew) {
                    this.setState({ selfAppraisalId: res.result?.selfAppraisalId });
                }
                if (this.state.medicalReportFile) {
                    this.setState({ medicalReportFileName: this.state.medicalReportFile.name, medicalReportFile: null });
                }
                this.setState({ isSubmitted: true });
                alert('Self Appraisal submitted successfully!');
            }
        });
    };

    render() {
        const {
            showLoader, selfAppraisalId, isSubmitted,
            briefResponsibilities,
            exceptionalContribution, constraintsHindered,
            trainingCurrentAssignment, trainingFutureCareer,
            immovablePropertyFiled, immovablePropertyDate,
            medicalCheckUp, medicalReportFile, medicalReportFileName,
            annualWorkPlanSet, showAppendixModal, validationErrors,
            propertyReturnYear, propertyReturnRecord,
        } = this.state;

        const locationState = this.props.location?.state || {};
        const selectedRecord = locationState.selectedRecord;
        const selectedCycle = selectedRecord?.cycle;
        const isCycleActive = selectedRecord?.periodStatus === 'Active';

        return (
            <div className="container mx-auto px-4 py-3">
                <div className="row">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">

                                <div className="row mb-3">
                                    <div className="col-md-8">
                                        <h1 className="page-title">Self Appraisal - Details</h1>
                                        {selectedRecord && (
                                            <p className="text-muted mb-0">
                                                <strong>{selectedCycle?.cycle_name || selectedCycle?.financial_year}</strong>
                                                &nbsp;·&nbsp;
                                                {formatDateDDMMYY(selectedRecord.startDate)}{selectedRecord.endDate ? ` to ${formatDateDDMMYY(selectedRecord.endDate)}` : ' onwards'}
                                                &nbsp;·&nbsp;
                                                <span className={`badge ${selectedRecord.isOriginal ? 'badge-success' : 'badge-warning'}`}>
                                                    {selectedRecord.periodType}
                                                </span>
                                            </p>
                                        )}
                                        {selfAppraisalId && (
                                            <span className="badge badge-success mt-1">Saved</span>
                                        )}
                                    </div>
                                    <div className="col-md-4 text-right">
                                        <button
                                            type="button"
                                            className="btn btn-sm cancel-button"
                                            onClick={() => this.props.history.goBack()}
                                        >
                                            <i className="fa fa-arrow-left mr-2" />Back
                                        </button>
                                    </div>
                                </div>

                                {!isCycleActive && !isSubmitted && (
                                    <div className="alert alert-info mt-2">
                                        This appraisal cycle is not active yet. Fields are view-only.
                                    </div>
                                )}

                                {isSubmitted && (
                                    <div className="alert alert-success mt-2">
                                        <i className="fa fa-check-circle mr-1" /> Self appraisal has been submitted and is now view-only.
                                    </div>
                                )}

                                {showLoader && <Loader />}

                                <form onSubmit={this.onSubmit}>

                                    {/* Section 1 */}
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">
                                            1. Brief description of responsibilities:
                                        </label>
                                        <p className="text-muted small mb-2">
                                            (Objectives of the position you hold and the responsibilities you are required to discharge, in about 100 words)
                                        </p>
                                        <textarea name="briefResponsibilities" rows={4} className={`form-control ${validationErrors.briefResponsibilities ? 'is-invalid' : ''}`} maxLength={700} value={briefResponsibilities} onChange={this.onTextChange} placeholder="Describe your responsibilities..." disabled={!isCycleActive || isSubmitted} />
                                        <small className="text-muted">{this.countWords(briefResponsibilities)} / 100 words</small>
                                    </div>

                                    {/* Section 2 */}
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">
                                            2. Exceptional Contribution:
                                        </label>
                                        <p className="text-muted small mb-2">
                                            During the period under report, do you believe that you have made any exceptional contribution, e.g. successful completion of an extraordinarily challenging task or major systemic improvement (resulting in significant benefits to the Company and/or reduction in time and costs)? If so, please give a verbal description (within 100 words):
                                        </p>
                                        <textarea name="exceptionalContribution" rows={4} className={`form-control ${validationErrors.exceptionalContribution ? 'is-invalid' : ''}`} maxLength={700} value={exceptionalContribution} onChange={this.onTextChange} placeholder="Describe your exceptional contribution..." disabled={!isCycleActive || isSubmitted} />
                                        <small className="text-muted">{this.countWords(exceptionalContribution)} / 100 words</small>
                                    </div>

                                    {/* Section 3 */}
                                    <div className="mb-4">
                                        <label className="form-label fw-semibold">
                                            3. Constraints that Hindered Performance:
                                        </label>
                                        <p className="text-muted small mb-2">What are the constraints that hindered your performance?</p>
                                        <textarea name="constraintsHindered" rows={4} className={`form-control ${validationErrors.constraintsHindered ? 'is-invalid' : ''}`} maxLength={700} value={constraintsHindered} onChange={this.onTextChange} placeholder="Describe the constraints..." disabled={!isCycleActive || isSubmitted} />
                                        <small className="text-muted">{this.countWords(constraintsHindered)} / 100 words</small>
                                    </div>

                                    {/* Section 4 */}
                                    <div className="mb-2">
                                        <label className="form-label fw-semibold">4. Please indicate specific areas of training that will add value to you:</label>
                                    </div>
                                    <div className="mb-4">
                                        <div className="border rounded p-3 mb-3">
                                            <label className="form-label fw-semibold small">For the current assignment:</label>
                                            <textarea name="trainingCurrentAssignment" rows={3} className={`form-control ${validationErrors.trainingCurrentAssignment ? 'is-invalid' : ''}`} maxLength={700} value={trainingCurrentAssignment} onChange={this.onTextChange} placeholder="Training needs for current assignment..." disabled={!isCycleActive || isSubmitted} />
                                            <small className="text-muted">{this.countWords(trainingCurrentAssignment)} / 100 words</small>
                                        </div>
                                        <div className="border rounded p-3">
                                            <label className="form-label fw-semibold small">For your future career:</label>
                                            <textarea name="trainingFutureCareer" rows={3} className={`form-control ${validationErrors.trainingFutureCareer ? 'is-invalid' : ''}`} maxLength={700} value={trainingFutureCareer} onChange={this.onTextChange} placeholder="Training needs for future career..." disabled={!isCycleActive || isSubmitted} />
                                            <small className="text-muted">{this.countWords(trainingFutureCareer)} / 100 words</small>
                                        </div>
                                    </div>

                                    {/* Declaration */}
                                    <h2 className="fs-5 fw-bold border-bottom pb-2 mb-4">Declaration</h2>
                                    <div className="table-responsive mb-4">
                                        <table className="table table-bordered align-middle">
                                            <tbody>
                                                <tr>
                                                    <td style={{ width: '55%' }}>Have you filed your immovable property return in the prescribed format as due? If yes, please mention the date.</td>
                                                    <td style={{ width: '15%' }}>
                                                        <div className={`d-flex gap-3 ${validationErrors.immovablePropertyFiled ? 'border border-danger rounded p-1' : ''}`}>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="radio" name="immovablePropertyFiled" value="yes" id="propYes" checked={immovablePropertyFiled === 'yes'} readOnly disabled />
                                                                <label className="form-check-label" htmlFor="propYes">Yes</label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="radio" name="immovablePropertyFiled" value="no" id="propNo" checked={immovablePropertyFiled === 'no'} readOnly disabled />
                                                                <label className="form-check-label" htmlFor="propNo">No</label>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ width: '30%' }}>
                                                        {immovablePropertyFiled === 'yes' ? (
                                                            <>
                                                                <input type="date" className={`form-control form-control-sm ${validationErrors.immovablePropertyDate ? 'is-invalid' : ''}`} name="immovablePropertyDate" value={immovablePropertyDate} readOnly disabled />
                                                                {propertyReturnRecord && (
                                                                    <small className="text-muted">Auto-filled from your property return for {propertyReturnYear}</small>
                                                                )}
                                                            </>
                                                        ) : immovablePropertyFiled === 'no' && (
                                                            <small className="text-danger">
                                                                Property return not filed{propertyReturnYear ? ` for ${propertyReturnYear}` : ''}.
                                                                {!isSubmitted && <> Please submit it from the <a href={CONSTANT.component.EmpPropertyReturn.url}>Property Return</a> page.</>}
                                                            </small>
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        Have you undergone the suggested medical check up?
                                                        <br />
                                                        <a
                                                            href="#appendix"
                                                            className="text-primary fw-semibold"
                                                            style={{ fontSize: '0.85rem' }}
                                                            onClick={(e) => { e.preventDefault(); this.setState({ showAppendixModal: true }); }}
                                                        >
                                                            <i className="fa fa-external-link mr-1" />Appendix I
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <div className={`d-flex gap-3 ${validationErrors.medicalCheckUp ? 'border border-danger rounded p-1' : ''}`}>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="radio" name="medicalCheckUp" value="yes" id="medYes" checked={medicalCheckUp === 'yes'} onChange={this.onTextChange} disabled={!isCycleActive || isSubmitted} />
                                                                <label className="form-check-label" htmlFor="medYes">Yes</label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="radio" name="medicalCheckUp" value="no" id="medNo" checked={medicalCheckUp === 'no'} onChange={this.onTextChange} disabled={!isCycleActive || isSubmitted} />
                                                                <label className="form-check-label" htmlFor="medNo">No</label>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {medicalCheckUp === 'yes' && (
                                                            <div>
                                                                <label className="form-label small fw-semibold mb-1">Upload Medical Report <span className="text-danger">*</span></label>
                                                                <input
                                                                    type="file"
                                                                    className={`form-control form-control-sm ${validationErrors.medicalReport ? 'is-invalid' : ''}`}
                                                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                                    onChange={(e) => {
                                                                        const file = e.target.files?.[0] || null;
                                                                        this.setState({ medicalReportFile: file });
                                                                    }}
                                                                    disabled={!isCycleActive || isSubmitted}
                                                                />
                                                                {medicalReportFileName && !medicalReportFile && (
                                                                    <small className="text-success d-block mt-1">
                                                                        <i className="fa fa-check-circle mr-1" />
                                                                        Uploaded: {medicalReportFileName}
                                                                    </small>
                                                                )}
                                                                {medicalReportFile && (
                                                                    <small className="text-info d-block mt-1">
                                                                        <i className="fa fa-file mr-1" />
                                                                        Selected: {medicalReportFile.name}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>Have you set the annual work plan for all officers for the current year, in respect of whom you are the Reporting Authority?</td>
                                                    <td>
                                                        <div className={`d-flex gap-3 ${validationErrors.annualWorkPlanSet ? 'border border-danger rounded p-1' : ''}`}>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="radio" name="annualWorkPlanSet" value="yes" id="workPlanYes" checked={annualWorkPlanSet === 'yes'} onChange={this.onTextChange} disabled={!isCycleActive || isSubmitted} />
                                                                <label className="form-check-label" htmlFor="workPlanYes">Yes</label>
                                                            </div>
                                                            <div className="form-check">
                                                                <input className="form-check-input" type="radio" name="annualWorkPlanSet" value="no" id="workPlanNo" checked={annualWorkPlanSet === 'no'} onChange={this.onTextChange} disabled={!isCycleActive || isSubmitted} />
                                                                <label className="form-check-label" htmlFor="workPlanNo">No</label>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-end gap-2">
                                        <button type="button" className="btn btn-outline-secondary" onClick={() => this.props.history.goBack()}>
                                            Back
                                        </button>
                                        {!isSubmitted && isCycleActive && (
                                            <button type="button" className="btn btn-secondary" onClick={this.handleSaveDraft} disabled={showLoader}>
                                                {showLoader ? 'Saving...' : 'Save Draft'}
                                            </button>
                                        )}
                                        {!isSubmitted && (
                                            <button type="submit" className="btn btn-primary" disabled={showLoader || !isCycleActive}>
                                                <i className="fa fa-paper-plane mr-1" />Submit Self Appraisal
                                            </button>
                                        )}
                                    </div>

                                </form>

                                {/* Appendix I Modal */}
                                <Modal isOpen={showAppendixModal} toggle={() => this.setState({ showAppendixModal: false })} size="lg">
                                    <ModalHeader toggle={() => this.setState({ showAppendixModal: false })}>
                                        Summary of Medical Report
                                    </ModalHeader>
                                    <ModalBody>
                                        <table className="table table-bordered">
                                            <tbody>
                                                <tr>
                                                    <td style={{ width: '10%' }} className="fw-semibold">1.</td>
                                                    <td style={{ width: '50%' }}>Overall Health of the officer</td>
                                                    <td style={{ width: '40%' }}></td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">2.</td>
                                                    <td>Any other remarks based on the health check up of the officer</td>
                                                    <td></td>
                                                </tr>
                                                <tr>
                                                    <td className="fw-semibold">3.</td>
                                                    <td>Health profile grading</td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </ModalBody>
                                </Modal>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter(SelfAppraisalDetails);
